import React, { useRef, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { getTime, startOfDay, endOfDay } from 'date-fns';
import DatePicker from 'react-datepicker';

import styles from './dateFilter.module.scss';
import {
  useClickOutside,
  formatDate,
  formatMonthDate,
  useFixedDropdownPosition,
} from '../../../utils';
import { Button, Icon } from '../..';
import RangeButton from './rangeButton/rangeButton';
import FilterTitle from '../_elements/filterTitle/filterTitle';

const DateFilter = ({
  title,
  lt,
  setLt,
  gt,
  setGt,
  onSubmit,
  sortOrder,
  setSortOrder,
  name,
  position,
  inLocations,
  disableSorting,
  isActive,
  titleWidth,
  disabled,
  icon = 'filter',
  iconClass = '',
  ranges,
  showMonthYearPicker,
  dateFormat = 'yyyy.MM.dd',
}) => {
  const filterRef = useRef();
  const dropdownRef = useRef();
  const [isOpen, setIsOpen] = useClickOutside(filterRef);
  const [shouldSort, setShouldSort] = useState(false);
  const dropdownStyle = useFixedDropdownPosition({
    triggerRef: filterRef,
    dropdownRef,
    isOpen,
    position,
  });
  const [currentRange, setCurrentRange] = useState({
    start: lt || 0,
    end: gt || 0,
  });

  const handleTitleClick = () => {
    if (disabled) {
      return;
    }

    if (!isOpen) {
      setShouldSort(false);
    }
    isOpen ? setIsOpen(false) : setIsOpen(true);
  };

  const handleOrderClick = () => {
    if (disabled) {
      return;
    }

    setShouldSort(true);
    sortOrder === 'asc' ? setSortOrder('desc') : setSortOrder('asc');
  };

  const handleSubmit = () => {
    if (disabled) {
      return;
    }

    onSubmit(shouldSort ? { sortBy: name } : {});
    isOpen ? setIsOpen(false) : setIsOpen(true);
  };

  const setRange = ({ start, end }) => {
    setGt(start);
    setLt(end);

    setCurrentRange({
      start: getTime(start),
      end: getTime(end),
    });
  };

  const datePickerChange = (date, setFilter, point) => {
    const timestamp = getTime(
      point === 'start' ? startOfDay(date) : endOfDay(date)
    );

    setFilter(timestamp);

    setCurrentRange({
      ...currentRange,
      [point]: timestamp,
    });
  };

  const rangeButtons = [
    { name: 'Šiandien', range: 'today' },
    { name: 'Vakar', range: 'yesterday' },
    { name: 'Rytoj', range: 'tomorrow' },
    { name: 'Ši savaitė', range: 'thisWeek' },
    { name: 'Praėjusi savaitė', range: 'lastWeek' },
    { name: 'Šis mėnuo', range: 'thisMonth' },
    { name: 'Praėjęs mėnuo', range: 'lastMonth' },
    { name: 'Šie metai', range: 'thisYear' },
    { name: 'Praėję metai', range: 'lastYear' },
  ];

  const renderRangeButtons = () =>
    (ranges || rangeButtons).map(({ name, range }) => (
      <RangeButton
        key={name}
        name={name}
        range={range}
        setRange={setRange}
        currentRange={
          lt === undefined || lt === 0
            ? {
                start: 0,
                end: 0,
              }
            : currentRange
        }
      />
    ));

  return (
    <div className={styles.container} ref={filterRef}>
      {inLocations ? (
        <div className={styles.locationsHeader} onClick={handleTitleClick}>
          {lt || gt ? (
            `${(showMonthYearPicker ? formatMonthDate : formatDate)(
              currentRange.start
            )} -
            ${(showMonthYearPicker ? formatMonthDate : formatDate)(
              currentRange.end
            )}`
          ) : (
            <div className={cn(styles.title, styles.rangeTitle)}>
              <p>{title}</p>
              <Icon
                name={icon}
                classes={{ icon: cn(styles.icon, iconClass) }}
              />
            </div>
          )}
        </div>
      ) : (
        <FilterTitle
          title={title}
          onTitleClick={handleTitleClick}
          isActive={isActive}
          customWidth={titleWidth}
          disabled={disabled}
        />
      )}

      {isOpen && (
        <div
          ref={dropdownRef}
          className={cn(styles.filter, styles[position])}
          style={dropdownStyle}
        >
          <div className={styles.datepicker}>
            <DatePicker
              className={styles.input}
              selected={gt}
              onChange={date => datePickerChange(date, setGt, 'start')}
              showYearDropdown
              placeholderText="Nuo"
              yearDropdownItemNumber={2}
              popperPlacement="bottom-center"
              dateFormat={dateFormat}
              disabled={disabled}
              showMonthYearPicker={!!showMonthYearPicker}
            />
            <DatePicker
              className={styles.input}
              selected={lt}
              onChange={date => datePickerChange(date, setLt, 'end')}
              showYearDropdown
              placeholderText="Iki"
              yearDropdownItemNumber={2}
              popperPlacement="bottom-center"
              dateFormat={dateFormat}
              disabled={disabled}
              showMonthYearPicker={showMonthYearPicker}
            />
          </div>

          <div className={styles.ranges}>{renderRangeButtons()}</div>

          <div className={styles.actions}>
            {!inLocations && !disableSorting && (
              <Button
                color="light"
                classes={{ button: styles.button }}
                onClick={handleOrderClick}
                disabled={disabled}
              >
                {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
              </Button>
            )}
            <Button
              color="primary"
              classes={{ button: styles.button }}
              onClick={handleSubmit}
              disabled={disabled}
            >
              Filtruoti
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

DateFilter.defaultProps = {
  position: 'left',
  lt: undefined,
  gt: undefined,
  inLocations: false,
  disabled: false,
};

DateFilter.propTypes = {
  title: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  lt: PropTypes.number,
  setLt: PropTypes.func.isRequired,
  gt: PropTypes.number,
  setGt: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  sortOrder: PropTypes.oneOf(['asc', 'desc']),
  setSortOrder: PropTypes.func,
  position: PropTypes.string,
  inLocations: PropTypes.bool,
  disableSorting: PropTypes.bool,
  isActive: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  icon: PropTypes.string,
  iconClass: PropTypes.string,
  ranges: PropTypes.array,
  showMonthYearPicker: PropTypes.bool,
  dateFormat: PropTypes.string,
};

export default DateFilter;
